import {
  branchListPath,
  businessProfilePath,
  capacitySettingsPath,
  fulfilmentSettingsPath,
  getStoredBranchId,
  openingHoursPath,
  serviceAreaPath,
  useBranchIdParam,
} from '../features/branch-settings/branchSettingsState.js';
import {
  bulkImportPath,
  catalogueOverviewPath,
  categoryListPath,
  modifierGroupListPath,
  productListPath,
} from '../features/catalogue/catalogueState.js';
import {
  deductionsAndDisputesPath,
  financeOverviewPath,
  financeReportsPath,
  financeTransactionsPath,
  payoutsPath,
  statementsPath,
} from '../features/finance/financeState.js';
import {
  batchAndExpiryPath,
  lowStockAlertsPath,
  stockOverviewPath,
} from '../features/inventory/inventoryState.js';
import {
  businessSettingsPath,
  exportCentrePath,
  integrationsOverviewPath,
  notificationSettingsPath,
  operationsReportPath,
  productPerformancePath,
  reviewListPath,
  salesReportPath,
  tradingDashboardPath,
} from '../features/reviews-analytics/reviewsAnalyticsState.js';
import { staffListPath } from '../features/staff-pin/staffPinState.js';
import { currentMerchantScope } from './merchantContext.js';
import {
  orderBoardPath,
  orderHistoryMerchantPath,
  scheduledOrdersPath,
} from '../features/order-board/orderBoardState.js';
import { floorModeSwitcherPath } from '../features/floor-mode/floorModeState.js';
import {
  filterCatalogueSubnavItems,
  filterFinanceSubnavItems,
  filterInventorySubnavItems,
  filterOrdersSubnavItems,
  filterReportsSubnavItems,
  filterSettingsSubnavItems,
  getMerchantRoleCodes,
} from './merchantNavAccess.js';
import { SectionSubnav, type SectionNavItem } from './SectionSubnav.js';

function resolveBranchId(): string | null {
  return getStoredBranchId() ?? currentMerchantScope().branchId ?? null;
}

function branchItems(
  items: { label: string; testId: string; path: (id: string) => string }[],
  branchId = resolveBranchId(),
): SectionNavItem[] {
  if (!branchId) return [];
  return items.map((item) => ({
    label: item.label,
    testId: item.testId,
    to: item.path(branchId),
  }));
}

/** 07-03 Settings nest only. Closures (014) stay a modal; extras stay on parent screens. */
function buildSettingsItems(branchId = resolveBranchId()): SectionNavItem[] {
  return [
    {
      to: businessProfilePath(),
      label: 'Profile',
      testId: 'settings-nav-profile',
      group: 'estate',
    },
    { to: branchListPath(), label: 'Branches', testId: 'settings-nav-branches', group: 'estate' },
    ...branchItems(
      [
        { label: 'Hours', testId: 'settings-nav-hours', path: openingHoursPath },
        { label: 'Service area', testId: 'settings-nav-service-area', path: serviceAreaPath },
        { label: 'Fulfilment', testId: 'settings-nav-fulfilment', path: fulfilmentSettingsPath },
        { label: 'Capacity', testId: 'settings-nav-capacity', path: capacitySettingsPath },
        {
          label: 'Delivery bands',
          testId: 'settings-nav-delivery-bands',
          path: (id) => `/delivery-bands?branchId=${encodeURIComponent(id)}`,
        },
      ],
      branchId,
    ).map((item) => ({ ...item, group: 'estate' })),
    {
      to: notificationSettingsPath(),
      label: 'Notifications',
      testId: 'settings-nav-notifications',
      group: 'ops',
    },
    {
      to: businessSettingsPath(),
      label: 'Tax and printing',
      testId: 'settings-nav-tax-printing',
      group: 'ops',
    },
    {
      to: integrationsOverviewPath(),
      label: 'Integrations',
      testId: 'settings-nav-integrations',
      group: 'integrations',
    },
    {
      to: '/vat-registration',
      label: 'VAT registration',
      testId: 'settings-nav-vat',
      group: 'ops',
    },
  ];
}

/** Finance landings. Owner-only BeatQ Pay is distinct from pre-live onboarding. */
const FINANCE_ITEMS: SectionNavItem[] = [
  { to: financeOverviewPath(), label: 'Overview', testId: 'finance-nav-overview' },
  {
    to: financeTransactionsPath(),
    label: 'Transactions',
    testId: 'finance-nav-transactions',
  },
  { to: statementsPath(), label: 'Statements', testId: 'finance-nav-statements' },
  {
    to: financeReportsPath(),
    label: 'Settlement reports',
    testId: 'finance-nav-reports',
  },
  { to: payoutsPath(), label: 'Payouts', testId: 'finance-nav-payouts' },
  { to: '/finance-invoices', label: 'Invoices', testId: 'finance-nav-invoices' },
  { to: '/finance/beat-pay', label: 'BeatQ Pay', testId: 'finance-nav-beat-pay' },
  {
    to: '/finance/chargebacks',
    label: 'Chargebacks',
    testId: 'finance-nav-chargebacks',
  },
  {
    to: deductionsAndDisputesPath(),
    label: 'Deductions',
    testId: 'finance-nav-deductions',
  },
];

const REPORTS_ITEMS: SectionNavItem[] = [
  { to: tradingDashboardPath(), label: 'Dashboard', testId: 'reports-nav-dashboard' },
  { to: salesReportPath(), label: 'Sales', testId: 'reports-nav-sales' },
  { to: operationsReportPath(), label: 'Operations', testId: 'reports-nav-operations' },
  { to: productPerformancePath(), label: 'Products', testId: 'reports-nav-products' },
  { to: exportCentrePath(), label: 'Export centre', testId: 'reports-nav-export' },
];

export const MERCHANT_REPORTS_SUBNAV = REPORTS_ITEMS;

export function getMerchantReportsSubnav(roles: readonly string[]): SectionNavItem[] {
  return filterReportsSubnavItems(REPORTS_ITEMS, roles);
}

/** 07-03 single-landing sections (Promotions, Reviews) omit a one-item subnav. */
export function collapseSingleLandingSubnav(items: SectionNavItem[]): SectionNavItem[] {
  return items.length <= 1 ? [] : items;
}

const CATALOGUE_ITEMS: SectionNavItem[] = [
  { to: catalogueOverviewPath(), label: 'Overview', testId: 'menu-nav-overview' },
  { to: categoryListPath(), label: 'Categories', testId: 'menu-nav-categories' },
  { to: productListPath(), label: 'All products', testId: 'menu-nav-products' },
  { to: modifierGroupListPath(), label: 'Modifier groups', testId: 'menu-nav-modifiers' },
  { to: bulkImportPath(), label: 'Bulk import', testId: 'menu-nav-import' },
  { to: '/branch-overrides', label: 'Branch overrides', testId: 'menu-nav-overrides' },
];

/** 07-03: Overview, Low stock, Batch. Adjust / Snooze / Bulk / Stocktake nest from overview. */
function buildInventoryItems(branchId = resolveBranchId()): SectionNavItem[] {
  const overview: SectionNavItem = {
    to: stockOverviewPath(branchId ?? undefined),
    label: 'Overview',
    testId: 'inventory-nav-overview',
  };
  if (!branchId) return [overview];
  return [
    overview,
    { to: lowStockAlertsPath(branchId), label: 'Low stock', testId: 'inventory-nav-alerts' },
    { to: batchAndExpiryPath(branchId), label: 'Batch & expiry', testId: 'inventory-nav-batch' },
    {
      to: `/stocktake?branchId=${encodeURIComponent(branchId)}`,
      label: 'Stocktake',
      testId: 'inventory-nav-stocktake',
    },
    {
      to: `/bulk-availability?branchId=${encodeURIComponent(branchId)}`,
      label: 'Bulk availability',
      testId: 'inventory-nav-bulk',
    },
  ];
}

/** 07-03 Team landing is SCR-MER-059 only; invite/detail nest from the list. */
const TEAM_ITEMS: SectionNavItem[] = [
  { to: staffListPath(), label: 'Staff', testId: 'team-nav-staff' },
];

export const MERCHANT_TEAM_SUBNAV = collapseSingleLandingSubnav(TEAM_ITEMS);

/** 07-03 Reviews landing is SCR-MER-073 only; 074/075 stay nested from the list. */
const REVIEWS_ITEMS: SectionNavItem[] = [
  { to: reviewListPath(), label: 'Review list', testId: 'reviews-nav-list' },
];

export const MERCHANT_REVIEWS_SUBNAV = collapseSingleLandingSubnav(REVIEWS_ITEMS);

const ORDERS_ITEMS: SectionNavItem[] = [
  { to: orderBoardPath(), label: 'Order board', testId: 'orders-nav-board' },
  { to: orderHistoryMerchantPath(), label: 'Order history', testId: 'orders-nav-history' },
  { to: scheduledOrdersPath(), label: 'Scheduled orders', testId: 'orders-nav-scheduled' },
  { to: floorModeSwitcherPath(), label: 'Floor modes', testId: 'orders-nav-floor' },
];

export const MERCHANT_ORDERS_SUBNAV = ORDERS_ITEMS;
export const MERCHANT_CATALOGUE_SUBNAV = CATALOGUE_ITEMS;
export const MERCHANT_FINANCE_SUBNAV = FINANCE_ITEMS;

export function getMerchantOrdersSubnav(roles: readonly string[]): SectionNavItem[] {
  return filterOrdersSubnavItems(ORDERS_ITEMS, roles);
}

export function getMerchantCatalogueSubnav(roles: readonly string[]): SectionNavItem[] {
  return filterCatalogueSubnavItems(CATALOGUE_ITEMS, roles);
}

export function getMerchantInventorySubnav(
  roles: readonly string[],
  branchId?: string | null,
): SectionNavItem[] {
  return filterInventorySubnavItems(buildInventoryItems(branchId ?? resolveBranchId()), roles);
}

export function getMerchantFinanceSubnav(roles: readonly string[]): SectionNavItem[] {
  return filterFinanceSubnavItems(FINANCE_ITEMS, roles);
}

export function getMerchantSettingsSubnav(
  roles: readonly string[] = [],
  branchId?: string | null,
): SectionNavItem[] {
  return filterSettingsSubnavItems(buildSettingsItems(branchId ?? resolveBranchId()), roles);
}
export const MERCHANT_SETTINGS_SUBNAV = getMerchantSettingsSubnav();

export function SettingsSubnav() {
  const branchId = useBranchIdParam();
  return (
    <SectionSubnav
      items={getMerchantSettingsSubnav(getMerchantRoleCodes(), branchId)}
      ariaLabel="Settings"
      testId="merchant-settings-subnav"
    />
  );
}

export function FinanceSubnav() {
  return (
    <SectionSubnav
      items={getMerchantFinanceSubnav(getMerchantRoleCodes())}
      ariaLabel="Finance"
      testId="merchant-finance-subnav"
    />
  );
}

export function ReportsSubnav() {
  const items = getMerchantReportsSubnav(getMerchantRoleCodes());
  return <SectionSubnav items={items} ariaLabel="Reports" testId="merchant-reports-subnav" />;
}

export function CatalogueSubnav() {
  return (
    <SectionSubnav
      items={getMerchantCatalogueSubnav(getMerchantRoleCodes())}
      ariaLabel="Catalogue"
      testId="merchant-menu-subnav"
    />
  );
}

export function MenuSubnav() {
  return <CatalogueSubnav />;
}

export function InventorySubnav() {
  const branchId = useBranchIdParam();
  return (
    <SectionSubnav
      items={getMerchantInventorySubnav(getMerchantRoleCodes(), branchId)}
      ariaLabel="Inventory"
      testId="merchant-inventory-subnav"
    />
  );
}

export function TeamSubnav() {
  return (
    <SectionSubnav items={MERCHANT_TEAM_SUBNAV} ariaLabel="Team" testId="merchant-team-subnav" />
  );
}

export function ReviewsSubnav() {
  return (
    <SectionSubnav
      items={MERCHANT_REVIEWS_SUBNAV}
      ariaLabel="Reviews"
      testId="merchant-reviews-subnav"
    />
  );
}

export function OrdersSubnav() {
  return (
    <SectionSubnav
      items={getMerchantOrdersSubnav(getMerchantRoleCodes())}
      ariaLabel="Orders"
      testId="merchant-orders-subnav"
    />
  );
}
